import * as Api from '@ViewModels';
import { ICustomSurvey, IRichContentEditorState } from '@ViewModels';
import * as yup from 'yup';
import {
	createBaseSurveySchema,
	createCustomFormFieldSchema,
	createNotificationSettingsSchema,
	createTagGenerationSettingsSchema,
} from '../shared/validation/schemas';

// Form type that allows IRichContentEditorState for form fields but matches the expected structure
export type CustomSurveyFormData = Omit<Partial<ICustomSurvey>, 'options'> & {
	updateExistingContacts?: boolean;
	options?: {
		contactNameOptions?: Api.IContactFieldOptions;
		contactEmailOptions?: Api.IContactFieldOptions;
		contactPhoneNumberOptions?: Api.IContactFieldOptions;
		contactBirthdayOptions?: Api.IContactFieldOptions;
		contactAddressOptions?: Api.IContactFieldOptions;
		intro?: IRichContentEditorState;
		companyName?: string;
		sendConfirmationMessage?: boolean;
		includeCompanyLogo?: boolean;
		confirmationMessage?: IRichContentEditorState;
	};
	tagGenerationSettings?: {
		tagOnCompletion?: boolean;
		completionTag?: string;
		customFormFieldTagSettings?: {
			fieldId: string;
			shouldGenerateTag: boolean;
			tagPrefix: string;
		}[];
		effectiveCompletionTag?: Api.IAccountTag;
	};
};

export const validationSchema = yup.object({
	// Base survey fields using shared schema (CustomSurvey doesn't require intro/dates)
	...createBaseSurveySchema({
		requireIntro: false,
		requireStartDate: false,
		requireExpirationDate: false,
	}),

	// CustomSurvey-specific options
	options: yup.object({
		contactNameOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactEmailOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactPhoneNumberOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactBirthdayOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactAddressOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		sendConfirmationMessage: yup.boolean().optional().default(false),
		intro: yup.mixed<IRichContentEditorState>().optional(),
		companyName: yup.string().optional(),
		includeCompanyLogo: yup.boolean().optional().default(false),
		image: yup.string().optional(),
		confirmationMessage: yup.mixed<IRichContentEditorState>().optional(),
	}),

	// Custom form with CustomSurvey-specific requirements
	customForm: yup.object({
		name: yup.string(),
		startDate: yup.date(),
		fields: createCustomFormFieldSchema({
			requireLabels: true, // CustomSurvey allows flexible label requirements
		}),
	}),

	// Shared notification settings (no ratings for CustomSurvey)
	notificationSettings: createNotificationSettingsSchema().optional(),

	// Shared tag generation settings
	tagGenerationSettings: createTagGenerationSettingsSchema().optional(),

	// CustomSurvey-specific fields
	hasDateRange: yup.boolean().optional().default(false),
});

export const TabValues = {
	Questions: 'Questions',
	Settings: 'Settings',
	Preview: 'Preview',
} as const;

export type TabValues = (typeof TabValues)[keyof typeof TabValues];
